import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronUp, LogOut, User, Settings, Star, Users } from 'lucide-react';
import { NsKey } from 'enums/NsKey';
import profile from 'assets/images/backgrounds/dashboard/profile.svg';
import { useCurrentAccess } from 'features/access/hooks/useCurrentAccess';

const frontUrl = process.env.REACT_APP_FRONT_URL;

interface DashboardMenuUserDropdownProps {
  isCollapsed: boolean;
}

export const DashboardMenuUserDropdown: React.FC<
  DashboardMenuUserDropdownProps
> = ({ isCollapsed }) => {
  const { t } = useTranslation(NsKey.Common);
  const auth = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (auth.user) {
      const uniqueName =
        auth.user.profile.unique_name ||
        auth.user.profile.preferred_username?.toString();
      setUserName(uniqueName ? uniqueName.toString() : '');
      setUserEmail(auth.user?.profile.email || '');
    }
  }, [auth.user]);

  // Fermer le dropdown quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      await auth.signoutRedirect({
        post_logout_redirect_uri: `${frontUrl}/bord`
      });
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      auth.removeUser();
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = `${frontUrl}/bord`;
    }
  };

  const handleMyAccount = () => {
    navigate('/mon-compte');
    setIsOpen(false);
  };

  const handleMyTeam = () => {
    navigate('/mon-equipe');
    setIsOpen(false);
  };

  const handleMyPortefeuille = () => {
    navigate('/mon-portefeuille-partage');
    setIsOpen(false);
  };

  const { plateformes } = useCurrentAccess();
  const hasOwnedPlateforme = plateformes.some((o) => o.gouverne);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="relative px-3 py-4" ref={dropdownRef}>
      {/* Dropdown menu - apparait au dessus */}
      {isOpen && (
        <div
          className={`absolute bottom-full mb-2 ${isCollapsed ? 'left-1/2 -translate-x-1/2' : 'left-3 right-3'}
          bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50 min-w-[200px]`}
        >
          {/* Header du dropdown avec infos utilisateur */}
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <p className="font-medium text-gray-900 truncate">{userName}</p>
            <p className="text-sm text-gray-500 truncate">{userEmail}</p>
          </div>

          {/* Options du menu */}
          <div className="py-1">
            <button
              onClick={handleMyAccount}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <User className="w-4 h-4" />
              <span className="text-sm font-medium">
                {t('SIDEBAR.MY_ACCOUNT')}
              </span>
            </button>

            <button
              onClick={handleMyTeam}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Users className="w-4 h-4" />
              <span className="text-sm font-medium">Mon équipe</span>
            </button>

            {hasOwnedPlateforme && (
              <button
                onClick={handleMyPortefeuille}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <Star className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-medium">Portefeuille partagé</span>
              </button>
            )}

            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm font-medium">
                {t('COMMON.LABELS.LOGOUT')}
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Bouton principal */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center gap-3 p-2 rounded-xl transition-all duration-200
          ${isOpen ? 'bg-white/20' : 'hover:bg-white/10'}
        `}
      >
        {/* Avatar */}
        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
          {userName === 'Confiancia' ? (
            <img
              src={profile}
              alt="profile"
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-white font-semibold text-sm">
              {getInitials(userName)}
            </span>
          )}
        </div>

        {/* Infos utilisateur (masquées si collapsed) */}
        {!isCollapsed && (
          <>
            <div className="flex-1 text-left min-w-0">
              <p className="text-white font-medium text-sm truncate">
                {userName}
              </p>
              <p className="text-white/60 text-xs truncate">{userEmail}</p>
            </div>

            {/* Chevron */}
            <ChevronUp
              className={`w-4 h-4 text-white/60 transition-transform duration-200 ${isOpen ? '' : 'rotate-180'}`}
            />
          </>
        )}
      </button>
    </div>
  );
};
